import type { AlertErrorHandlerFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { type Job } from '@/features/jobs/Job'
import { type TaxRefundMethodViewModel } from '@/features/jobs/info-gathering/tax-refund-method/TaxRefundMethodViewModel'
import { type TaxRefundMethodStore } from '@/features/jobs/info-gathering/tax-refund-method/TaxRefundMethodStore'
import { type InfoGatheringStore } from '@/features/jobs/info-gathering/InfoGatheringStore'
import { TaxRefundMethodEditorViewModel } from '@/features/jobs/info-gathering/tax-refund-method/TaxRefundMethodEditorViewModel'

/**
 * A tab of the Edit Refund dialog.
 */
export type RefundTab = 'personal' | 'business'

/**
 * The label of each tab.
 */
export const REFUND_TAB_LABELS: Record<RefundTab, string> = {
  personal: 'Personal',
  business: 'Business',
}

/**
 * View model for the dialog that edits the client's refund methods.
 */
export class EditRefundDialogViewModel {
  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel()

  /**
   * Editor for the personal refund method tab.
   */
  readonly personalRefundEditorVm = new TaxRefundMethodEditorViewModel(
    'Personal',
  )

  /**
   * Editor for the business refund method tab.
   */
  readonly businessRefundEditorVm = new TaxRefundMethodEditorViewModel(
    'Business',
  )

  /**
   * The tab being shown.
   */
  @observable accessor activeTab: RefundTab = 'personal'

  constructor(
    private readonly _job: Job,
    private readonly _getTaxRefundMethodVm: () => TaxRefundMethodViewModel | null,
    private readonly _taxRefundMethodStore: TaxRefundMethodStore,
    private readonly _infoGatheringStore: InfoGatheringStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * The tabs for the refund methods the checklist asks for.
   */
  @computed
  get availableTabs(): RefundTab[] {
    const tabs: RefundTab[] = []
    if (this._getTaxRefundMethodVm()?.personalRefundMethodSupportingItem) {
      tabs.push('personal')
    }
    if (this._getTaxRefundMethodVm()?.businessRefundMethodSupportingItem) {
      tabs.push('business')
    }
    return tabs
  }

  /**
   * Whether the refund methods have been fetched, so the dialog can be shown.
   */
  @computed
  get canShow() {
    return this._getTaxRefundMethodVm()?.isLoaded ?? true
  }

  /**
   * Whether more than one tab is available.
   */
  @computed
  get hasMultipleTabs() {
    return this.availableTabs.length > 1
  }

  /**
   * Whether the active tab is the first available one.
   */
  @computed
  get isFirstTab() {
    return this.activeTab === this.availableTabs[0]
  }

  /**
   * Whether the active tab is the last available one.
   */
  @computed
  get isLastTab() {
    return this.activeTab === this.availableTabs[this.availableTabs.length - 1]
  }

  /**
   * The tab after the active one, if any.
   */
  @computed
  get nextTab(): RefundTab | null {
    const index = this.availableTabs.indexOf(this.activeTab)
    if (index >= 0 && index < this.availableTabs.length - 1) {
      return this.availableTabs[index + 1]
    }
    return null
  }

  /**
   * The tab before the active one, if any.
   */
  @computed
  get previousTab(): RefundTab | null {
    const index = this.availableTabs.indexOf(this.activeTab)
    if (index > 0) {
      return this.availableTabs[index - 1]
    }
    return null
  }

  /**
   * The editor of the active tab.
   */
  @computed
  get activeEditor() {
    return this.activeTab === 'personal'
      ? this.personalRefundEditorVm
      : this.businessRefundEditorVm
  }

  /**
   * Switches to the tab.
   */
  @action.bound
  setTab(tab: RefundTab) {
    this.activeTab = tab
  }

  /**
   * Moves to the next tab without saving, closing the dialog after the last one.
   */
  @action.bound
  skipCurrentTab() {
    if (this.nextTab) {
      this.activeTab = this.nextTab
      return
    }
    this.modal.close()
  }

  /**
   * Moves to the previous tab.
   */
  @action.bound
  goToPreviousTab() {
    if (this.previousTab) {
      this.activeTab = this.previousTab
    }
  }

  /**
   * Shows the dialog on the personal tab.
   */
  @action.bound
  showPersonal() {
    this.show('personal')
  }

  /**
   * Shows the dialog on the business tab.
   */
  @action.bound
  showBusiness() {
    this.show('business')
  }

  /**
   * Shows the dialog with editors filled from the saved refund methods, on the tab if it is available.
   */
  @action.bound
  show(initialTab: RefundTab = 'personal') {
    this.personalRefundEditorVm.reset(
      this._getTaxRefundMethodVm()?.personalRefundMethod,
    )
    this.businessRefundEditorVm.reset(
      this._getTaxRefundMethodVm()?.businessRefundMethod,
    )
    this.activeTab = this.availableTabs.includes(initialTab)
      ? initialTab
      : (this.availableTabs[0] ?? 'personal')
    this.modal.show()
  }

  /**
   * Saves the active tab and moves to the next one, closing the dialog after the last one.
   */
  submit = task.resolved(async () => {
    if (!this.activeEditor.validation.reset().validate().isValid) {
      return
    }

    const saved = await (
      this.activeTab === 'personal'
        ? this._savePersonal()
        : this._saveBusiness()
    )
      .then(() => true)
      .catch(this._createAlertErrorHandler('Saving the refund method failed'))
      .catch(() => false)
    if (!saved) {
      return
    }

    const nextTab = this.nextTab
    if (nextTab) {
      runInAction(() => {
        this.activeTab = nextTab
      })
    } else {
      this.modal.close()
    }
  })

  private async _savePersonal() {
    const editor = this.personalRefundEditorVm
    const existing = this._getTaxRefundMethodVm()?.personalRefundMethod
    const deliveryMethod = editor.toDeliveryMethod()

    const saved = existing?.id
      ? await this._taxRefundMethodStore.updateTaxRefundMethod({
          id: existing.id,
          refundType: 'Personal',
          deliveryMethod,
        })
      : await this._taxRefundMethodStore.createTaxRefundMethod({
          workspaceId: this._job.workspace.id,
          ownerUserId: this._job.championClient!.member.user.publicId,
          refundType: 'Personal',
          deliveryMethod,
        })

    await this._infoGatheringStore.provideSupportingItemPersonalRefundMethod({
      jobId: this._job.id,
      itemId:
        this._getTaxRefundMethodVm()!.personalRefundMethodSupportingItem!.id,
      refundMethodId: saved.id,
      refundMethodVersion: saved.version,
    })
  }

  private async _saveBusiness() {
    const editor = this.businessRefundEditorVm
    const existing = this._getTaxRefundMethodVm()?.businessRefundMethod
    const deliveryMethod = editor.toDeliveryMethod()

    const saved = existing?.id
      ? await this._taxRefundMethodStore.updateTaxRefundMethod({
          id: existing.id,
          refundType: 'Business',
          deliveryMethod,
        })
      : await this._taxRefundMethodStore.createTaxRefundMethod({
          workspaceId: this._job.workspace.id,
          ownerUserId: this._job.championClient!.member.user.publicId,
          refundType: 'Business',
          deliveryMethod,
        })

    await this._infoGatheringStore.provideSupportingItemBusinessRefundMethod({
      jobId: this._job.id,
      itemId:
        this._getTaxRefundMethodVm()!.businessRefundMethodSupportingItem!.id,
      refundMethodId: saved.id,
      refundMethodVersion: saved.version,
    })
  }
}
